/**
 * 前端 ZIP 文件处理工具
 * 用于请求和解压缩 ZIP 压缩的 JSON 文件
 */

import { useState } from 'react';

export interface ClaimDataItem {
  userTVMAddress: string;
  rewardAmount: string;
  rewardAmountInEther: string;
  merkleProof: string[];
  leafHash: string;
  isEligible: boolean;
}

export type ClaimData = ClaimDataItem[];

/**
 * ZIP 客户端类，用于处理压缩文件的请求和解压缩
 */
export class ZipClient {
  /**
   * 请求并解压缩 ZIP 文件
   * @param url - 压缩文件的 URL
   * @param fileName - ZIP 文件中要提取的文件名，默认为 'claimData.json'
   * @returns 解压缩后的 JSON 数据
   */
  static async fetchAndDecompress<T = unknown>(url: string, fileName: string = 'claimData.json'): Promise<T> {
    try {
      // 请求压缩文件
      const response = await fetch(url);
      console.log(response);

      if (!response.ok) {
        throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
      }

      // 使用 JSZip 解压缩
      return await this.decompressWithJSZip(response, fileName);
    } catch (error) {
      console.error('Error fetching and decompressing file:', error);
      throw new Error(
        `Failed to fetch and decompress ${url}: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * 使用 JSZip 库解压缩 ZIP 文件
   * @param response - fetch 响应对象
   * @param fileName - 要提取的文件名
   * @returns 解压缩后的 JSON 数据
   */
  private static async decompressWithJSZip<T>(response: Response, fileName: string): Promise<T> {
    // 动态导入 JSZip 库
    const JSZip = (await import('jszip')).default;

    // 获取压缩数据
    const compressedData = await response.arrayBuffer();

    // 创建 JSZip 实例并加载数据
    const zip = new JSZip();
    const zipData = await zip.loadAsync(compressedData);

    // 查找目标文件
    const file = zipData.file(fileName);
    if (!file) {
      throw new Error(`File '${fileName}' not found in ZIP archive`);
    }

    // 提取文件内容
    const fileContent = await file.async('string');

    // 解析 JSON
    return JSON.parse(fileContent);
  }

  /**
   * 专门用于获取 claimData 的便捷方法
   * @param url - claimData.json.zip 文件的 URL，默认为 '/claimData.json.zip'
   * @param fileName - ZIP 文件中的文件名，默认为 'claimData.json'
   * @returns ClaimData 数组
   */
  static async fetchClaimData(
    url: string = '/claimData.json.zip',
    fileName: string = 'claimData.json'
  ): Promise<ClaimData> {
    return await this.fetchAndDecompress<ClaimData>(url, fileName);
  }

  /**
   * 列出 ZIP 文件中的所有文件
   * @param url - ZIP 文件的 URL
   * @returns 文件名列表
   */
  static async listFiles(url: string): Promise<string[]> {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
      }

      const JSZip = (await import('jszip')).default;
      const compressedData = await response.arrayBuffer();
      const zip = new JSZip();
      const zipData = await zip.loadAsync(compressedData);

      return Object.keys(zipData.files).filter((name) => !zipData.files[name].dir);
    } catch (error) {
      console.error('Error listing ZIP files:', error);
      throw new Error(`Failed to list files in ${url}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 获取文件大小信息（用于显示下载进度）
   * @param url - 文件 URL
   * @returns 文件大小（字节）
   */
  static async getFileSize(url: string): Promise<number> {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      const contentLength = response.headers.get('content-length');
      return contentLength ? parseInt(contentLength, 10) : 0;
    } catch (error) {
      console.warn('Failed to get file size:', error);
      return 0;
    }
  }

  /**
   * 检查 ZIP 文件中是否包含指定文件
   * @param url - ZIP 文件的 URL
   * @param fileName - 要检查的文件名
   * @returns 是否包含该文件
   */
  static async hasFile(url: string, fileName: string): Promise<boolean> {
    try {
      const files = await this.listFiles(url);
      return files.includes(fileName);
    } catch (error) {
      console.error('Error checking file existence:', error);
      return false;
    }
  }
}

/**
 * 带进度回调的 ZIP 文件下载和解压缩
 * @param url - 文件 URL
 * @param fileName - ZIP 文件中要提取的文件名
 * @param onProgress - 进度回调函数
 * @returns 解压缩后的数据
 */
export async function fetchAndDecompressWithProgress<T = unknown>(
  url: string,
  fileName: string = 'claimData.json',
  onProgress?: (loaded: number, total: number) => void
): Promise<T> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
  }

  const contentLength = response.headers.get('content-length');
  const total = contentLength ? parseInt(contentLength, 10) : 0;

  if (!response.body) {
    throw new Error('Response body is null');
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;

  // 读取数据并报告进度
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    chunks.push(value);
    loaded += value.length;

    if (onProgress && total > 0) {
      onProgress(loaded, total);
    }
  }

  // 合并数据
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const compressedData = new Uint8Array(totalLength);
  let offset = 0;

  for (const chunk of chunks) {
    compressedData.set(chunk, offset);
    offset += chunk.length;
  }

  // 使用 JSZip 解压缩
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  const zipData = await zip.loadAsync(compressedData.buffer);

  const file = zipData.file(fileName);
  if (!file) {
    throw new Error(`File '${fileName}' not found in ZIP archive`);
  }

  const fileContent = await file.async('string');
  return JSON.parse(fileContent);
}

/**
 * React Hook 用于加载 ZIP 压缩的 claimData
 * @param url - ZIP 文件的 URL
 * @param fileName - ZIP 文件中的文件名
 * @returns 包含数据、加载状态和错误信息的对象
 */
export function useZipClaimData(url: string = '/claimData.json.zip', fileName: string = 'claimData.json') {
  const [data, setData] = useState<ClaimData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    setProgress(0);

    try {
      const claimData = await fetchAndDecompressWithProgress<ClaimData>(url, fileName, (loaded, total) => {
        setProgress(Math.round((loaded / total) * 100));
      });
      setData(claimData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return {
    data,
    loading,
    error,
    progress,
    loadData,
    reload: loadData
  };
}

// 导出默认实例
export default ZipClient;
